import * as React from 'react';
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type SortingState
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { FC, useMemo, useRef, useState } from 'react';
import classes from './QualityReportTable.module.scss';
import Skeleton from '../../../Skeleton/Skeleton';
import { QualityReportEmployeeRow } from '../../../../../types/Admin/QualityReport';
import {
  formatQualityReportAverage,
  formatQualityReportCount
} from '../../../../../utils/Admin/AnalyticsAndReports/formatQualityReportValue';

type QualityReportTableProps = {
  employees: QualityReportEmployeeRow[];
  totals: QualityReportEmployeeRow;
  isLoadingData: boolean;
};

const columnSizes = {
  employeeName: 220,
  ratingsCount: 140,
  averageRating: 140,
  score: 80
};

const averageSortValue = (value: number | null | undefined): number => {
  if (value == null) {
    return Number.NEGATIVE_INFINITY;
  }
  return value;
};

const QualityReportTable: FC<QualityReportTableProps> = ({
  employees,
  totals,
  isLoadingData
}) => {
  const columnHelper = createColumnHelper<QualityReportEmployeeRow>();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'employeeName', desc: false }]);
  const [activeRow, setActiveRow] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor('employeeName', {
        header: ({ column }) => (
          <div
            className={classes.sortableHeader}
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
            Сотрудник
            <span className={classes.sortIcon}>
              {column.getIsSorted() === 'asc' ? '↑' : column.getIsSorted() === 'desc' ? '↓' : ''}
            </span>
          </div>
        ),
        cell: ({ getValue }) => <div className={classes.colunmContent}>{getValue()}</div>,
        size: columnSizes.employeeName,
        sortingFn: 'alphanumeric'
      }),
      columnHelper.accessor('ratingsCount', {
        header: () => <div className={classes.headerContent}>Кол-во оценок</div>,
        cell: ({ getValue }) => (
          <div className={classes.colunmContent}>{formatQualityReportCount(getValue())}</div>
        ),
        size: columnSizes.ratingsCount,
        enableSorting: false
      }),
      columnHelper.accessor('averageRating', {
        header: ({ column }) => (
          <div
            className={classes.sortableHeader}
            onClick={() => {
              const current = column.getIsSorted();
              if (!current) {
                column.toggleSorting(true);
              } else {
                column.toggleSorting(current === 'asc');
              }
            }}>
            Средняя оценка
            <span className={classes.sortIcon}>
              {column.getIsSorted() === 'asc' ? '↑' : column.getIsSorted() === 'desc' ? '↓' : ''}
            </span>
          </div>
        ),
        cell: ({ getValue }) => (
          <div className={classes.colunmContent}>{formatQualityReportAverage(getValue())}</div>
        ),
        size: columnSizes.averageRating,
        sortingFn: (rowA, rowB, columnId) => {
          const a = averageSortValue(rowA.getValue(columnId) as number | null);
          const b = averageSortValue(rowB.getValue(columnId) as number | null);
          if (a === b) return 0;
          return a < b ? -1 : 1;
        }
      }),
      columnHelper.accessor('score1', {
        header: () => <div className={classes.headerContentCenter}>1</div>,
        cell: ({ getValue }) => (
          <div className={classes.columnContentCenter}>{formatQualityReportCount(getValue())}</div>
        ),
        size: columnSizes.score,
        enableSorting: false
      }),
      columnHelper.accessor('score2', {
        header: () => <div className={classes.headerContentCenter}>2</div>,
        cell: ({ getValue }) => (
          <div className={classes.columnContentCenter}>{formatQualityReportCount(getValue())}</div>
        ),
        size: columnSizes.score,
        enableSorting: false
      }),
      columnHelper.accessor('score3', {
        header: () => <div className={classes.headerContentCenter}>3</div>,
        cell: ({ getValue }) => (
          <div className={classes.columnContentCenter}>{formatQualityReportCount(getValue())}</div>
        ),
        size: columnSizes.score,
        enableSorting: false
      }),
      columnHelper.accessor('score4', {
        header: () => <div className={classes.headerContentCenter}>4</div>,
        cell: ({ getValue }) => (
          <div className={classes.columnContentCenter}>{formatQualityReportCount(getValue())}</div>
        ),
        size: columnSizes.score,
        enableSorting: false
      }),
      columnHelper.accessor('score5', {
        header: () => <div className={classes.headerContentCenter}>5</div>,
        cell: ({ getValue }) => (
          <div className={classes.columnContentCenter}>{formatQualityReportCount(getValue())}</div>
        ),
        size: columnSizes.score,
        enableSorting: false
      })
    ],
    [columnHelper]
  );

  const tableData = useMemo(() => {
    if (isLoadingData) {
      return Array(5).fill({}) as QualityReportEmployeeRow[];
    }
    return employees;
  }, [employees, isLoadingData]);

  const tableColumns = useMemo(
    () =>
      isLoadingData
        ? columns.map((column) => ({
            ...column,
            cell: () => <Skeleton />
          }))
        : columns,
    [columns, isLoadingData]
  );

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    state: { sorting },
    onSortingChange: setSorting,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  const rows = table.getRowModel().rows;
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 52,
    overscan: 12
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0;

  return (
    <div className={classes.tableContainer}>
      <div className={classes.tableScroll} ref={scrollRef}>
        <table className={classes.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} colSpan={header.colSpan} style={{ width: `${header.getSize()}px` }}>
                    <div className={classes.thTitle}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      <div
                        onDoubleClick={() => header.column.resetSize()}
                        onMouseDown={header.getResizeHandler()}
                        className={`resizer ltr ${header.column.getIsResizing() ? 'isResizing' : ''}`}>
                        <div className={classes.colorResizer} />
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} colSpan={columns.length} />
              </tr>
            )}
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              return (
                <tr
                  key={row.id}
                  onClick={() => setActiveRow(row.id)}
                  className={activeRow === row.id ? classes.activeRow : ''}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} colSpan={columns.length} />
              </tr>
            )}
          </tbody>
          {!isLoadingData && (
            <tfoot>
              <tr className={classes.totalRow}>
                <td>
                  <div className={classes.colunmContent}>{totals.employeeName}</div>
                </td>
                <td>
                  <div className={classes.colunmContent}>
                    {formatQualityReportCount(totals.ratingsCount)}
                  </div>
                </td>
                <td>
                  <div className={classes.colunmContent}>
                    {formatQualityReportAverage(totals.averageRating)}
                  </div>
                </td>
                <td>
                  <div className={classes.columnContentCenter}>
                    {formatQualityReportCount(totals.score1)}
                  </div>
                </td>
                <td>
                  <div className={classes.columnContentCenter}>
                    {formatQualityReportCount(totals.score2)}
                  </div>
                </td>
                <td>
                  <div className={classes.columnContentCenter}>
                    {formatQualityReportCount(totals.score3)}
                  </div>
                </td>
                <td>
                  <div className={classes.columnContentCenter}>
                    {formatQualityReportCount(totals.score4)}
                  </div>
                </td>
                <td>
                  <div className={classes.columnContentCenter}>
                    {formatQualityReportCount(totals.score5)}
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default QualityReportTable;
